import { KeyBadge } from "./KeyBadge";

/**
 * Once-per-pilot onboarding banners shown over the canvas.
 *
 *   <MoonstoneTipBanner /> — first-Moonstone-pickup hint (Endless economy).
 *   <HotkeyTipBanner />    — first-fullscreen-entry hotkey legend.
 *
 * Both extracted from `GameCanvas.jsx` on 2026-05-13. Each component:
 *   - Self-gates on its `visible` prop so the parent can mount it
 *     unconditionally.
 *   - Persists dismissal to localStorage under a stable key so it never
 *     re-appears on future runs.
 *   - Calls back to the parent (`onDismiss`) so the parent can clear
 *     its own state ref + cancel any auto-dismiss timer.
 *
 * The parent owns the SHOW logic (`showHotkeyTip` / `showMoonstoneTip`)
 * because it's tangled into the wallet-load / fullscreen-enter
 * lifecycle — we keep that here only as a render concern.
 */

const TIP_BASE_CLS =
  "absolute left-1/2 -translate-x-1/2 top-3 sm:top-4 flex items-center gap-2 pointer-events-auto z-20 fade-in";
const TIP_CARD_BASE_CLS =
  "border bg-black/80 backdrop-blur-sm px-3 py-2 flex items-center gap-3";
const TIP_DISMISS_CLS =
  "ml-1 text-white/50 hover:text-white text-sm leading-none";

export function MoonstoneTipBanner({ visible, onDismiss }) {
  if (!visible) return null;
  return (
    <div
      className={TIP_BASE_CLS}
      data-testid="moonstone-tip-banner"
      role="status"
    >
      <div
        className={`${TIP_CARD_BASE_CLS} border-[#ffd700]/60`}
        style={{ boxShadow: "0 0 22px rgba(255,215,0,0.28)" }}
      >
        <span
          className="font-mono-cosmic text-[14px] leading-none"
          style={{
            color: "#ffd700",
            textShadow: "0 0 8px rgba(255,215,0,0.8)",
          }}
          aria-hidden="true"
        >
          ☾
        </span>
        <div className="min-w-0 max-w-[28rem]">
          <div className="font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-[#ffd700]">
            First Moonstone
          </div>
          <div className="text-white/70 text-[11px] font-mono-cosmic leading-snug">
            Spend these in the Moonstone Store to unlock paddle skins &amp; starter perks.
          </div>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className={TIP_DISMISS_CLS}
          aria-label="Dismiss moonstone tip"
          data-testid="moonstone-tip-dismiss"
        >
          ×
        </button>
      </div>
    </div>
  );
}

const HOTKEYS = [
  ["P", "#00ffff", "Pause"],
  ["R", "#ffd700", "Restart"],
  ["K", "#b026ff", "Skins"],
  ["Q", "#ff007f", "Quit"],
];

export function HotkeyTipBanner({ visible, onDismiss }) {
  if (!visible) return null;
  return (
    <div
      className={TIP_BASE_CLS}
      data-testid="hotkey-tip-banner"
      role="status"
    >
      <div
        className={`${TIP_CARD_BASE_CLS} border-[#00ffff]/60`}
        style={{ boxShadow: "0 0 22px rgba(0,255,255,0.25)" }}
      >
        <span className="font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-[#00ffff]">
          Hotkeys
        </span>
        {HOTKEYS.map(([k, color, label]) => (
          <div key={k} className="flex items-center gap-1.5">
            <KeyBadge k={k} color={color} />
            <span className="text-white/60 text-[10px] font-mono-cosmic">
              {label}
            </span>
          </div>
        ))}
        <button
          type="button"
          onClick={onDismiss}
          className={TIP_DISMISS_CLS}
          aria-label="Dismiss hotkey tip"
          data-testid="hotkey-tip-dismiss"
        >
          ×
        </button>
      </div>
    </div>
  );
}
